/**
 * GS1 Application-Identifier parser for КИЗ DataMatrix codes.
 *
 * Honest-Sign DM payloads always start with `01<GTIN-14>21<serial>`, followed
 * by an AI 93/91 + 4-char crypto check. Two layouts in the wild:
 *
 *  A. With GS separator (0x1d) between serial and the next AI — canonical:
 *       `01 04650002548633 21 DTaeQ6jmr7&M  93 dGVz`
 *
 *  B. Without GS (some scanners strip control chars, and short serials make it
 *     parse OK without one) — what the user's example uses:
 *       `01 04650002548633 21 5x7RFk 93 JhHH`
 *
 * Strategy:
 *   - Read AI 01 + 14-digit GTIN.
 *   - Require AI 21 immediately after.
 *   - If the rest contains a GS, serial = bytes up to the GS; then parse the
 *     remaining AI 93|91 + 4 crypto bytes.
 *   - Otherwise the rest looks like `<serial><AI 93|91><4 crypto bytes>`.
 *     Parse it from the END: the last six bytes are `9X<4 bytes>` where 9X is
 *     93 or 91. Take everything before those six as serial.
 *
 * The earlier "scan forward until we see 93/91" was wrong — real ЧЗ serials
 * contain those digit pairs and the parser would silently cut them short.
 */
export interface DataMatrixPayload {
  gtin: string;
  serial: string;
  crypto?: string;
}

const GS = "\x1d";
//: Человекочитаемая форма GS1: `(01)…` в самом начале строки.
export const HRI_START = /^\(\d{2,4}\)/;
const SERIAL_MAX = 20;
const CRYPTO_LEN = 4;
// Подпись ГИСМ (AI 92) — base64 фиксированной длины. Нужна, чтобы отрезать
// хвост, когда сканер съел разделители: без этого «читаем шесть байт с конца»
// упирается в середину подписи и код не разбирается вовсе.
const SIGNATURE_LEN = 44;
const SIGNATURE_RE = new RegExp(`92[A-Za-z0-9+/=]{${SIGNATURE_LEN}}$`);
const CRYPTO_RE = new RegExp(`9[13].{${CRYPTO_LEN}}$`);

export function parseDataMatrix(raw: string): DataMatrixPayload | null {
  if (!raw) return null;
  // GS1 «human-readable representation» с круглыми скобками вокруг AI
  // (некоторые сканеры в УНФ выводят именно в таком виде):
  //   (01)04650002542891(21)5B;IEv(93AbCd
  // Или даже без значения после последнего AI:
  //   (01)04650002542891(21)5B;IEv(93
  // Парсим отдельным путём — там разделители между AI не нужны.
  //
  // ⚠️ Признак формы — СТРОКА НАЧИНАЕТСЯ с `(AI)`, а не «где-то есть скобки»:
  // скобки входят в алфавит серийников ЧЗ, и боевой код `…215ph)(F<GS>93EfrK`
  // (PS-526, 16.09.2026) по подстроке `)(` уходил в скобочный разбор и
  // отвергался целиком — «Код не распознан» на исправной этикетке.
  if (HRI_START.test(raw)) {
    return parseHumanReadable(raw);
  }

  if (raw.length < 2 + 14 + 2 + 1) return null;
  if (raw.slice(0, 2) !== "01") return null;

  const gtin = raw.slice(2, 16);
  if (!/^\d{14}$/.test(gtin)) return null;

  if (raw.slice(16, 18) !== "21") return null;

  const tail = raw.slice(18);

  const gsIdx = tail.indexOf(GS);
  let serial: string;
  let crypto: string | undefined;

  if (gsIdx !== -1) {
    serial = tail.slice(0, gsIdx);
    const after = tail.slice(gsIdx + 1);
    crypto = parseCryptoAi(after);
  } else {
    // Разделителей нет — отрезаем известные хвостовые группы С КОНЦА, по одной.
    // Сначала подпись ГИСМ (92 + 44 base64), затем проверочный код (91|93 + 4).
    // Что осталось — серийник. Хвоста может не быть вовсе: короткая форма
    // `01<GTIN>21<серийник>` встречается на живых бирках, и раньше она
    // отвергалась целиком.
    let rest = tail.replace(SIGNATURE_RE, "");
    const cryptoMatch = rest.match(CRYPTO_RE);
    if (cryptoMatch) {
      crypto = cryptoMatch[0].slice(2);
      rest = rest.slice(0, rest.length - cryptoMatch[0].length);
    }
    serial = rest;
  }

  if (!serial || serial.length > SERIAL_MAX) return null;
  if (!/^[!-~]+$/.test(serial)) return null;

  return { gtin, serial, crypto };
}

function parseCryptoAi(after: string): string | undefined {
  if (after.length < 2 + CRYPTO_LEN) return undefined;
  const ai = after.slice(0, 2);
  // 93 — фарма-ЧЗ, 91 — ГИСМ (за ним отдельной группой идёт подпись 92,
  // которая нам не нужна: в ЧЗ-сервис уходит серийник).
  if (ai !== "93" && ai !== "91") return undefined;
  return after.slice(2, 2 + CRYPTO_LEN);
}

/**
 * Парсер human-readable формы `(AI)value(AI)value...`.
 *
 * Берём пары `(\d{2,4})<value>` — value тянется до следующей `(` или
 * конца строки. Crypto (AI 93/91) опционален: некоторые сканеры
 * заканчивают payload на `(93` без значения; для нашего флоу важен
 * только gtin+serial (ЧЗ-lookup делается по serial).
 */
function parseHumanReadable(raw: string): DataMatrixPayload | null {
  // Регекс читает AI в скобках + всё до СЛЕДУЮЩЕЙ открывающей `(` или
  // конца строки. `[^(]*` пустое поле тоже допускает (например `(93`
  // без значения после).
  const re = /\((\d{2,4})\)?([^(]*)/g;
  const pairs: Record<string, string> = {};
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw)) !== null) {
    // На `(93` без `)` regex захватит `(93` и `[^(]*=""` — записываем
    // пустое значение, ниже crypto станет undefined.
    const ai = m[1];
    if (!ai) continue;
    pairs[ai] = (m[2] || "").trim();
  }

  const gtin = pairs["01"];
  const serial = pairs["21"];
  if (!gtin || !/^\d{14}$/.test(gtin)) return null;
  if (!serial || serial.length > SERIAL_MAX) return null;
  if (!/^[!-~]+$/.test(serial)) return null;

  const cryptoRaw = pairs["93"] ?? pairs["91"];
  const crypto = cryptoRaw ? cryptoRaw.slice(0, CRYPTO_LEN) : undefined;
  return { gtin, serial, crypto };
}

/**
 * Best-effort discriminator between EAN13 and DataMatrix payloads at scan time.
 *
 * - 13 digits → EAN13.
 * - Starts with "01" + 14 digits → likely DataMatrix.
 * - Otherwise unknown.
 */
export function detectScanKind(raw: string): "ean13" | "datamatrix" | "unknown" {
  if (/^\d{13}$/.test(raw)) return "ean13";
  if (raw.startsWith("01") && raw.length >= 16 && /^\d{14}$/.test(raw.slice(2, 16))) {
    return "datamatrix";
  }
  // human-readable: `(01)<14d>...`
  if (/^\(01\)\d{14}/.test(raw)) return "datamatrix";
  return "unknown";
}
